// The rounds-mode app root — `App.tsx`'s sibling for the other mounted surface (rounds-slice
// implementation-review **F1**).
//
// Same shape as `App`, one substitution: `BrowserRouter → RoundsController → AppRoutes` instead of
// `BrowserRouter → SessionController → AppRoutes`. The app-root audio layer is installed through
// the shared `useAppAudioController` hook, so both roots carry exactly one audio controller with
// the same lifetime and teardown rather than two subtly different copies.
//
// Kept a separate root rather than a branch inside `App` for the same reason the server keeps
// `rounds_app` separate from `app` (design **D1**): the two surfaces share no state. `App` takes a
// `SessionControllerDeps` bundle built around a WebSocket transport, a match session store and a
// world renderer; none of that exists here, and a mode branch inside the live root would mean the
// live path grows a conditional for a mode it never runs in.

import type { ReactElement } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AudioRouteObserver, useAppAudioController } from '../../audio/init-audio';
import { RoundsController, type RoundsControllerDeps } from './RoundsController';

export function RoundsApp(props: RoundsControllerDeps): ReactElement {
  const audio = useAppAudioController();
  return (
    <BrowserRouter>
      {audio !== null && <AudioRouteObserver controller={audio} />}
      <RoundsController {...props} sound={audio?.sound} />
    </BrowserRouter>
  );
}
